<template>
  <form class="form form--participating-insurers participating-insurers" :method="method" :action="action">

    <!-- Initial state selection field. -->
    <div class="form__field">
      <label for="pi-state" class="label-big text-center">
        First, tell us where your health insurance plan is based:
      </label>

      <select class="form-control"
              name="state"
              id="pi-state"
              ref="stateSelect"
              v-on:change="handleStateChange"
              required
              data-required-message="Please select a state."
      >
        <option value="">Select State</option>
        <option v-for="(name, abbr) in states" v-bind:key="name" :value="abbr">
          {{ name }}
        </option>
      </select>
    </div>
    <!-- State selection field end. -->

    <!-- Next step, participating insurers based upon selected state. -->
    <div class="participating-insurers__insurers" v-if="showInsurers">
      <p class="text-center w-75">Great News! Many of our products are covered by insurance plans in {{ selectedStateLabel }}.</p>

      <div class="form-field">

        <label for="pi-insurer-name" class="text-center label-big">
          What is the name of your health insurance plan?
        </label>

        <div class="participating-insurers__name-container">
          <!--
          This input is the center of the second step, helping the user to find an insurer in their state.

          All of the events have different roles and reasons. It's important that we help the user to find
          their insurer and make sure the most accurate value is in this input before the form is submitted.
          -->
          <input type="text"
                 name="pi-insurer-name"
                 id="pi-insurer-name"
                 class="form-control"
                 autocomplete="off"
                 placeholder="Enter your insurance carrier"
                 required
                 data-required-message="Please enter your insurance carrier."
                 aria-haspopup="listbox"
                 aria-controls="insurer-suggestions"
                 aria-owns="insurer-suggestions"
                 :aria-expanded="insurerSuggestions.length > 0"
                 aria-activedescendant=""
                 ref="insurerTextInput"
                 v-on:keyup="handleInsurerNameInputKeyup($event)"
                 v-on:keydown="handleInsurerNameInputKeydown($event)"
                 v-on:focus="handleInsurerNameInputFocus"
                 v-on:blur="handleInsurerNameInputBlur($event)"
                 v-on:input="handleInsurerNameInputChange"
          />

          <!--
          Insurer suggestions. The suggestion labels are linked to the radio buttons in the insurer list below.
          Additionally, we want these to be navigable via tab.
          -->
          <ul v-if="insurerSuggestions.length > 0"
              class="participating-insurers__suggestion-list"
              id="insurer-suggestions"
              role="listbox">
            <li v-for="(suggestion, index) in insurerSuggestions" v-bind:key="index">
              <label class="participating-insurers__suggestion"
                     tabindex="0"
                     role="option"
                     :id="`insurer-suggestion-${index}`"
                     :for="`insurer-${suggestion.insurerName}`"
                     :data-insurer-name="suggestion.insurerName"
                     v-html="suggestion.insurerNameHighlighted"
              />
            </li>
          </ul>
          <!-- End insurer suggestions. -->

        </div>

      </div>

      <!-- All insurance carriers based on selected state. -->
      <lms-revelator :label="`View all carriers in ${selectedStateLabel}`">
        <ul class="participating-insurers__insurers-list">
          <li v-for="insurer in stateInsurers" v-bind:key="insurer">
            <label class="participating-insurers__insurer-label"
                   tabindex="0"
                   :for="`insurer-${insurer}`"
                   :id="`insurer-${insurer}-label`">
              <span>{{ insurer }}</span>
            </label>

            <!--
            With both the suggestion labels and the insurer list labels linking to their related radio buttons,
            the change event here directs a lot of what happens after a user makes some kind of decision
            on selecting their insurer.
            -->
            <input
              type="radio"
              name="insurer"
              class="participating-insurers__insurer_choice"
              :id="`insurer-${insurer}`"
              :value="insurer"
              v-on:change="handleInsurerSelected($event)"
              v-on:click="handleInsurerSelected($event)" />
          </li>
        </ul>
      </lms-revelator>

    </div>

    <!-- Intended to display a message when no insurer is selected. -->
    <slot name="no-insurer" v-if="showNoInsurerSlot"></slot>

    <!-- Displays after a user has selected an insurer, or typed a match to an existing insurer. -->
    <div v-if="showSelectedInsurerSlot">
      <p class="text-center">{{ selectedInsurer }} is one of our participating health insurance plans!</p>
      <slot name="selected-insurer" ></slot>
    </div>

    <!-- Additional form fields. -->
    <slot v-if="showAdditionalFieldsSlot"></slot>

    <button type="submit" class="button btn-block">Submit</button>
  </form>
</template>

<script lang="js">
  const LmsRevelator = () =>
    import(
      /* webpackChunkName: "revelator-lazy" */ /* webpackPrefetch: true */ "../revelator/revelator.vue"
      );

  export default  {
    name: 'lms-participating-insurers',
    components: {
      LmsRevelator,
    },
    props: {
      method: {
        type: String,
        default: 'post',
      },
      action: {
        type: String,
        default: '',
      },
      insurerData: {
        type: Object,
        default: () => {return {};},
      },
    },
    data() {
      return {
        states: {},
        stateInsurers: [],
        showNoInsurerSlot: false,
        showSelectedInsurerSlot: false,
        showAdditionalFieldsSlot: false,
        selectedState: '',
        selectedStateLabel: '',
        showInsurers: false,
        insurerSuggestions: [],
        selectedInsurer: '',
      }
    },
    mounted () {
      const vm = this;

      vm.states = vm.insurerData.states;
    },
    methods: {
      /**
       * Handles the state dropdown change event.
       */
      handleStateChange() {
        const vm = this;
        const stateSelect = vm.$refs.stateSelect;

        // Initialize some values and reset the state of different UI elements.
        vm.selectedState = stateSelect.value;
        vm.selectedStateLabel = '';
        vm.stateInsurers = [];
        vm.showInsurers = false;

        vm.resetSuggestions();
        vm.resetSelection();

        if (vm.selectedState !== '') {
          // Only if we have a state value selected to we proceed to retrieve
          // the insurers for the state.
          vm.selectedStateLabel = stateSelect.options[stateSelect.selectedIndex].text;

          vm.getInsurersByState();

          vm.showInsurers = true;
          vm.showNoInsurerSlot = true;
          vm.showAdditionalFieldsSlot = true;
        }

      },
      /**
       * Handles when an insurer radio button is checked/changes.
       *
       * @param event
       */
      handleInsurerSelected(event) {
        const insurerRadio = event.target;

        // First, remove all existing selected states from the labels.
        document
          .querySelectorAll('.participating-insurers__insurer-label')
          .forEach((labelElement) => {
            labelElement.classList.remove('selected');
          });

        // Now, find the associated label of the radio button that fired the event.
        let insurerRadioLabel = document.querySelector(`[id="${insurerRadio.id}-label"]`);

        // Add the selected class.
        insurerRadioLabel.classList.add('selected');

        // Lastly, update the input text box and reset any suggestions.
        this.$refs.insurerTextInput.value = insurerRadio.value;
        this.$refs.insurerTextInput.dispatchEvent(new Event('input'));
        this.resetSuggestions();
        this.handleInsurerNameInputChange();
      },
      /**
       * Keyup event handler for the insurer name text input.
       *
       * @param event
       */
      handleInsurerNameInputKeyup(event) {
        // Will have to handle different key presses.
        const vm = this;
        const key = event.keyCode;
        const insurerNameInput = vm.$refs.insurerTextInput;

        switch (key) {
          // Enter.
          case 13:
            event.preventDefault();

            break;

          // Escape.
          case 27:
            insurerNameInput.blur();

            break;

          default:
            // Execute the suggestion search.
            vm.searchForInsurers(event.target.value);

            break;
        }
      },
      /**
       * Keydown is needed to handle navigating to the suggestions list, and selecting
       * a suggestion via enter, but maintaing focus in the insurer name text input.
       *
       * @param event
       */
      handleInsurerNameInputKeydown(event) {
        const vm = this;
        const key = event.keyCode;
        const insurerNameInput = vm.$refs.insurerTextInput;

        switch (key) {
          case 13: {
            event.preventDefault();

            let currentHighlightedSuggestion = document.querySelector('.participating-insurers__suggestion.highlighted');

            if (currentHighlightedSuggestion !== null) {
              insurerNameInput.value = currentHighlightedSuggestion.dataset.insurerName;
              currentHighlightedSuggestion.click();
              vm.resetSuggestions();
            }

            break;
          }
          // Up arrow.
          case 38:
            vm.selectSuggestion('prev');

            break;

          // Down arrow.
          case 40:
            vm.selectSuggestion('next');

            break;
        }
      },
      /**
       * Handles when the insurer name text field changes.
       */
      handleInsurerNameInputChange() {
        let matchFound = false;
        let matchName = '';
        const enteredInsurer = this.$refs.insurerTextInput.value.toLowerCase();

        // See if the current value entered for the insurer name
        // matches any in the list.
        for (let i = 0; i < this.stateInsurers.length; i++) {
          const insurerName = this.stateInsurers[i];

          if (insurerName.toLowerCase() === enteredInsurer) {
            matchFound = true;
            matchName = insurerName;
            break;
          }
        }

        // If we have a match, show the "insurer found" messages.
        if (matchFound) {
          this.selectedInsurer = matchName;
          this.showNoInsurerSlot = false;
          this.showSelectedInsurerSlot = true;

          document.getElementById(`insurer-${matchName}`).click();
        }
        else {
          // Otherwise the default state.
          this.selectedInsurer = '';
          this.showNoInsurerSlot = true;
          this.showSelectedInsurerSlot = false;
          this.resetSelection();
        }
      },
      /**
       * Handles focus gain for the insurer name text field.
       */
      handleInsurerNameInputFocus() {
        const vm = this;

        vm.searchForInsurers(vm.$refs.insurerTextInput.value);
      },
      /**
       * Handles focus loss for the insurer name text field.
       *
       * @param event
       */
      handleInsurerNameInputBlur(event) {
        const vm = this;

        let nextFocusedElement = event.relatedTarget;

        if (nextFocusedElement === null || !nextFocusedElement.classList.contains('participating-insurers__suggestion')) {
          vm.resetSuggestions();
        }
        else {
          vm.handleInsurerNameInputChange();
        }

        this.$refs.insurerTextInput.removeAttribute('aria-activedescendant');
      },
      /**
       * Gets a list of insurers by state.
       */
      getInsurersByState() {
        const vm = this;

        let stateInsurers = [];

        vm.insurerData.insurers.forEach((insurer) => {
          for (let i = 0; i < insurer.states.length; i++) {

            if (vm.selectedState === insurer.states[i] || insurer.states[i].toLowerCase() === 'national') {
              // If this insurer exists in the selected state,
              // or if the insurer is not specific to a state,
              // add it to the list and move onto the next insurer.
              stateInsurers.push(insurer.name);

              break;
            }

          }
        });

        stateInsurers.sort((insurerA, insurerB) => {
          return insurerA.localeCompare(insurerB);
        });

        vm.stateInsurers = stateInsurers;
      },
      resetSuggestions() {
        this.insurerSuggestions = [];
      },
      resetSelection() {
        document.querySelectorAll('.participating-insurers__insurer_choice').forEach((element) => {
          element.checked = false;
        });

        const checkedRadio = document.querySelector('.participating-insurers__insurer-label.selected');

        if (checkedRadio !== null) {
          checkedRadio
            .classList
            .remove('selected');
        }

      },
      /**
       * Search for insurers based on input from the user.
       *
       * @param searchString
       */
      searchForInsurers(searchString) {
        const vm = this;

        if (searchString.length >= 2) {
          searchString = searchString.toLowerCase();

          vm.insurerSuggestions = [];

          // Let's loop through the state insurers, and see if there are any potential matches.
          // For now we only want a maximum of 3 suggestions.
          for (let i = 0; i < vm.stateInsurers.length && vm.insurerSuggestions.length < 5; i++) {
            let insurerName = vm.stateInsurers[i];
            const lowerInsurerName = insurerName.toLowerCase();

            if (lowerInsurerName.includes(searchString)) {
              // First, check to see if the search string is an exact match for the current insurer name.
              // If it is, no need to suggest it. Move to the next.
              if (searchString === lowerInsurerName) {
                continue;
              }

              // Let's providing some markup to highlight the string in the suggestion.
              const startIndex = lowerInsurerName.indexOf(searchString);
              let textToReplace = insurerName.substring(startIndex, startIndex + searchString.length);
              let highlightedText = `<strong>${textToReplace}</strong>`;

              // Now, replace the matched text with the highlight text.
              let insurerNameHighlighted = insurerName.replace(textToReplace, highlightedText);

              vm.insurerSuggestions.push({
                insurerName: insurerName,
                insurerNameHighlighted: insurerNameHighlighted,
              });
            }
          }
        }
        else {
          vm.resetSuggestions();
        }
      },
      /**
       * Handles navigating through the list of suggestions.
       *
       * @param direction
       *   Either `next` or `prev`.
       */
      selectSuggestion(direction) {
        const vm = this;

        // Get the currently highlighted suggestion, and the first and last suggestions in the list.
        let currentHighlightedSuggestion = document.querySelector('.participating-insurers__suggestion.highlighted');
        let suggestionElements = Array.from(document.querySelectorAll('.participating-insurers__suggestion'));
        let firstSuggestion = suggestionElements[0];
        let lastSuggestion = suggestionElements[suggestionElements.length - 1];

        if (currentHighlightedSuggestion !== null) {
          // There is a currently highlighted suggestion.
          // First, remove all existing highlights.
          vm.removeSuggestionHighlights();

          if (direction === 'next') {
            // Retrieve the next element.

            if (currentHighlightedSuggestion === lastSuggestion) {
              // If the current highlighted and last suggestion are the same, and we're moving to the next,
              // loop back to the first suggestion.
              vm.setSuggestionElementStatus(firstSuggestion, 'active');
            }
            else {
              // Highlight the next suggestion element.
              vm.setSuggestionElementStatus(currentHighlightedSuggestion.parentElement.nextElementSibling.firstElementChild, 'active');
            }
          }
          else {
            // The only other direction is previous.
            if (currentHighlightedSuggestion === firstSuggestion) {
              vm.setSuggestionElementStatus(lastSuggestion, 'active');
            }
            else {
              vm.setSuggestionElementStatus(currentHighlightedSuggestion.parentElement.previousElementSibling.firstElementChild, 'active');
            }
          }

        }
        else {
          // There is currently no suggestion highlighted. This means the user has yet to navigate into the list
          // of suggestions. In that case, we only allow for arrow down actions to enter the list.
          if (direction === 'next') {
            vm.setSuggestionElementStatus(firstSuggestion, 'active');
          }
        }
      },
      removeSuggestionHighlights() {
        document.querySelectorAll('.participating-insurers__suggestion').forEach((element) => {
          this.setSuggestionElementStatus(element, 'inactive');
        });
      },
      setSuggestionElementStatus(element, status) {
        let insurerTextInput = this.$refs.insurerTextInput;

        if (status === 'active') {
          element.classList.add('highlighted');
          element.setAttribute('aria-selected', 'true');
          insurerTextInput.setAttribute('aria-activedescendant', element.id);
        }
        else {
          element.classList.remove('highlighted');
          element.setAttribute('aria-selected', 'false');
          insurerTextInput.removeAttribute('aria-activedescendant');
        }
      },
    },
}
</script>
